
interface ComponentInstance {
  id: string;
  name: string;
  mountTime: number;
  renderCount: number;
  lastRender: number;
  props?: any;
  state?: any;
}

interface LoopDetection {
  functionName: string;
  calls: number;
  lastCall: number;
  callStack: string[];
}

class AppDebugger {
  private components: Map<string, ComponentInstance> = new Map();
  private loopDetections: Map<string, LoopDetection> = new Map();
  private apiCalls: Map<string, number> = new Map();
  private rerenderThreshold = 5; // Flag if component renders more than 5 times in 10 seconds
  private loopThreshold = 3; // Flag if function called more than 3 times in 5 seconds

  generateComponentId(componentName: string): string {
    const id = `${componentName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`🆔 Generated component ID: ${id}`);
    return id;
  }

  trackComponentMount(componentName: string, props?: any): string {
    const id = this.generateComponentId(componentName);
    const instance: ComponentInstance = {
      id,
      name: componentName,
      mountTime: Date.now(),
      renderCount: 1,
      lastRender: Date.now(),
      props: this.sanitizeProps(props)
    };
    
    this.components.set(id, instance);
    console.log(`🏗️ Component MOUNTED: ${componentName} (ID: ${id})`);
    this.checkForDuplicateComponents(componentName);
    return id;
  }

  trackComponentRender(componentId: string, props?: any, state?: any) {
    const instance = this.components.get(componentId);
    if (!instance) {
      console.warn(`⚠️ Tracking render for unknown component: ${componentId}`);
      return;
    }

    instance.renderCount++;
    instance.lastRender = Date.now();
    instance.props = this.sanitizeProps(props);
    instance.state = this.sanitizeState(state);

    console.log(`🔄 Component RENDER #${instance.renderCount}: ${instance.name} (ID: ${componentId})`);

    // Check for excessive re-renders
    if (instance.renderCount > this.rerenderThreshold) {
      const timeSinceMount = Date.now() - instance.mountTime;
      if (timeSinceMount < 10000) { // 10 seconds
        console.error(`🚨 EXCESSIVE RENDERS DETECTED: ${instance.name} rendered ${instance.renderCount} times in ${timeSinceMount}ms`);
        console.error(`Props:`, instance.props);
        console.error(`State:`, instance.state);
        this.printStackTrace();
      }
    }
  }

  trackComponentUnmount(componentId: string) {
    const instance = this.components.get(componentId);
    if (instance) {
      console.log(`🗑️ Component UNMOUNTED: ${instance.name} (ID: ${componentId}) - Total renders: ${instance.renderCount}`);
      this.components.delete(componentId);
    }
  }

  trackFunctionCall(functionName: string, args?: any) {
    const now = Date.now();
    const detection = this.loopDetections.get(functionName) || {
      functionName,
      calls: 0,
      lastCall: 0,
      callStack: []
    };

    detection.calls++;
    detection.lastCall = now;
    detection.callStack.push(new Error().stack?.split('\n')[2] || 'unknown');

    // Keep only recent call stack entries
    if (detection.callStack.length > 10) {
      detection.callStack = detection.callStack.slice(-10);
    }

    this.loopDetections.set(functionName, detection);

    // Check for loops - if called more than threshold times in 5 seconds
    const recentCalls = Array.from(this.loopDetections.values())
      .filter(d => d.functionName === functionName && (now - d.lastCall) < 5000);

    if (recentCalls.length > this.loopThreshold) {
      console.error(`🚨 POTENTIAL LOOP DETECTED: ${functionName} called ${detection.calls} times`);
      console.error(`Recent call stack:`, detection.callStack);
      console.error(`Args:`, args);
    }
  }

  trackAPICall(url: string, method: string) {
    const key = `${method} ${url}`;
    const count = this.apiCalls.get(key) || 0;
    this.apiCalls.set(key, count + 1);

    if (count > 2) { // More than 2 calls to the same endpoint
      console.warn(`🚨 REPEATED API CALL: ${key} called ${count + 1} times`);
      this.printStackTrace();
    }
  }

  private checkForDuplicateComponents(componentName: string) {
    const instances = Array.from(this.components.values())
      .filter(c => c.name === componentName);
    
    if (instances.length > 1) {
      console.error(`🚨 DUPLICATE COMPONENTS: ${instances.length} instances of ${componentName} are mounted!`);
      instances.forEach((instance, index) => {
        console.error(`  Instance ${index + 1}: ${instance.id} (mounted ${Date.now() - instance.mountTime}ms ago)`);
      });
    }
  }

  private sanitizeProps(props: any): any {
    if (!props) return undefined;
    
    // Remove functions and complex objects to avoid circular references
    const sanitized: any = {};
    Object.keys(props).forEach(key => {
      const value = props[key];
      if (typeof value === 'function') {
        sanitized[key] = '[Function]';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = '[Object]';
      } else {
        sanitized[key] = value;
      }
    });
    return sanitized;
  }

  private sanitizeState(state: any): any {
    if (!state) return undefined;
    return this.sanitizeProps(state);
  }

  private printStackTrace() {
    const stack = new Error().stack;
    console.error('📍 Call stack:', stack);
  }

  // This will be replaced by a separate hook

  // Debug report
  generateReport(): string {
    const report = {
      timestamp: new Date().toISOString(),
      components: Array.from(this.components.values()),
      loopDetections: Array.from(this.loopDetections.values()),
      apiCalls: Object.fromEntries(this.apiCalls),
      summary: {
        activeComponents: this.components.size,
        totalAPICallTypes: this.apiCalls.size,
        potentialLoops: Array.from(this.loopDetections.values()).filter(d => d.calls > this.loopThreshold).length
      }
    };

    console.log('📊 DEBUG REPORT:', report);
    return JSON.stringify(report, null, 2);
  }

  // Reset all tracking
  reset() {
    this.components.clear();
    this.loopDetections.clear();
    this.apiCalls.clear();
    console.log('🧹 Debug tracking reset');
  }

  // Public getters for debug commands
  getComponents() {
    return Array.from(this.components.values());
  }

  getLoopDetections() {
    return Array.from(this.loopDetections.values());
  }

  getAPICalls() {
    return Object.fromEntries(this.apiCalls);
  }
}

// Global instance
const appDebugger = new AppDebugger();

// Make it available globally for console debugging
(window as any).appDebugger = appDebugger;

// Global debug commands
(window as any).debugCommands = {
  report: () => appDebugger.generateReport(),
  reset: () => appDebugger.reset(),
  components: () => appDebugger.getComponents(),
  loops: () => appDebugger.getLoopDetections(),
  apis: () => appDebugger.getAPICalls(),
  help: () => {
    console.log(`
🛠️  Debug Commands:
  debugCommands.report() - Generate full debug report
  debugCommands.reset() - Reset all tracking
  debugCommands.components() - List all component instances
  debugCommands.loops() - List detected loops
  debugCommands.apis() - List API call counts
  debugCommands.help() - Show this help
`);
  }
};

// Show help on load
console.log('🛠️  Debug mode enabled! Type "debugCommands.help()" for available commands.');

export default appDebugger;